import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import {
  StyledButton,
  CustomInput,
  CustomCheckbox,
  DeliveryOption,
  PaymentOption,
  AccordionSection,
  CheckoutSummary,
  PageHeaderSection,
  CustomOffcanvas,
} from "../components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { loadFullCart } from "../redux/slices/fullCartSlice";
import {
  useCheckoutForm,
  usePickupStores,
  useDeliveryOptions,
  usePaymentOptions
} from "../hooks";
import PickupStoreModal from "./pickupStoreModal";

import bg_05 from "./../images/Content/haeussler-job-01.webp"

const Checkout: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const cartId = useSelector((state: RootState) => state.cart.cartId);
  const { products, total, taxes, pickUpFrom, pickUpUntill, requiredPreparationDays } = useSelector(
    (state: RootState) => state.fullCart
  );

  const {
    formData,
    shouldUseShippingAddressForBilling,
    setShouldUseShippingAddressForBilling,
    handleInputChange,
    handleNextStep,
  } = useCheckoutForm(() => {
    setIsAddressValid(true)
    setCurrentStep(2)
  });

  const {
    deliveryOptions,
    selectedDelivery,
    selectedDeliveryId,
    setSelectedDeliveryId,
  } = useDeliveryOptions();

  const {
    paymentOptions,
    selectedPayment,
    selectedPaymentId,
    setSelectedPaymentId,
  } = usePaymentOptions();

  const {
    stores,
    selectedStore,
    setSelectedStore,
    loading,
    error,
    reload
  } = usePickupStores();

  useEffect(() => {
    if (cartId) {
      dispatch(loadFullCart(cartId));
    }
  }, [dispatch, cartId]);

  const [showPickupModal, setShowPickupModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isAddressValid, setIsAddressValid] = useState(false);

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
  const isEmailValid = isValidEmail(formData.mail);

  const isContaktOpen = currentStep === 1;
  const isAdressOpen = currentStep === 1 && isEmailValid;
  const isDeliveryOpen = currentStep === 2 && isAddressValid;
  const isPaymentOpen = currentStep === 3 && isAddressValid;

  const goToStep = (step: number) => setCurrentStep(step);

  const handleNextFromDelivery = () => {
    setCurrentStep(3);
  };

  return (
    <Container>
      <PageHeaderSection
        id={"section_start"}
        headline={`${products.length} Artikel`}
        subheadline={"Kasse"}
        content={<></>}
      />
      <Row>
        <Col xl={7} lg={7} md={7} sm={12} xs={12}>
          <Row style={{ marginTop: 40 }}>
            <Col md={12} xs={12}>
              <AccordionSection
                isOpen={isContaktOpen}
                onHeaderClick={() => goToStep(1)}
                header="Kontakt"
              >
                <Row>
                  <Col xl={8}>
                    <CustomInput
                      name="mail"
                      label="E-Mail"
                      value={formData.mail}
                      onChange={handleInputChange}
                    />
                  </Col>
                </Row>
              </AccordionSection>
              <AccordionSection
                isOpen={isAdressOpen}
                onHeaderClick={() => goToStep(1)}
                header="Adresse"
              >
                <Row>
                  <Col xl={6}><CustomInput name="shipping_givenname" label="Vorname" value={formData.shipping_givenname} onChange={handleInputChange} /></Col>
                  <Col xl={6}><CustomInput name="shipping_surname" label="Nachname" value={formData.shipping_surname} onChange={handleInputChange} /></Col>
                  <Col xl={12}><CustomInput name="shipping_address1" label="Straße, Hausnummer" value={formData.shipping_address1} onChange={handleInputChange} /></Col>
                  <Col xl={12}><CustomInput name="shipping_address2" label="Adresszusatz" value={formData.shipping_address2} onChange={handleInputChange} /></Col>
                  <Col xl={6}><CustomInput name="shipping_postalCode" label="PLZ" value={formData.shipping_postalCode} onChange={handleInputChange} /></Col>
                  <Col xl={6}><CustomInput name="shipping_city" label="Ort" value={formData.shipping_city} onChange={handleInputChange} /></Col>
                  <Col xl={12}>
                    <CustomCheckbox
                      name="billing_checkbox"
                      label="Rechnungsadresse entspricht Lieferadresse"
                      defaultChecked={shouldUseShippingAddressForBilling}
                      onChange={() => setShouldUseShippingAddressForBilling(!shouldUseShippingAddressForBilling)}
                    />
                  </Col>
                  {!shouldUseShippingAddressForBilling && (
                    <>
                      <Col xl={6}><CustomInput name="billing_givenname" label="Vorname" value={formData.billing_givenname} onChange={handleInputChange} /></Col>
                      <Col xl={6}><CustomInput name="billing_surname" label="Nachname" value={formData.billing_surname} onChange={handleInputChange} /></Col>
                      <Col xl={12}><CustomInput name="billing_address1" label="Straße" value={formData.billing_address1} onChange={handleInputChange} /></Col>
                      <Col xl={12}><CustomInput name="billing_address2" label="Adresszusatz" value={formData.billing_address2} onChange={handleInputChange} /></Col>
                      <Col xl={6}><CustomInput name="billing_postalCode" label="PLZ" value={formData.billing_postalCode} onChange={handleInputChange} /></Col>
                      <Col xl={6}><CustomInput name="billing_city" label="Ort" value={formData.billing_city} onChange={handleInputChange} /></Col>
                    </>
                  )}
                </Row>
                <StyledButton title="Weiter" onClick={handleNextStep}/>
              </AccordionSection>
              <AccordionSection
                isOpen={isDeliveryOpen}
                header="Lieferung"
                onHeaderClick={() => goToStep(2)}
              >
                <>
                  {deliveryOptions.map(option => (
                    <DeliveryOption
                      key={option.id}
                      headline={option.headline}
                      price={`${option.price.toFixed(2)} €`}
                      info1={option.id === "pickup" ? (selectedStore?.name ?? "") : option.info1}
                      info2={option.id === "pickup" ? (`${selectedStore?.street_address}, ${selectedStore?.postal_code} ${selectedStore?.location}`) : option.info2}
                      imageSrc={option.imageSrc}
                      isSelected={selectedDeliveryId === option.id}
                      onClick={() => setSelectedDeliveryId(option.id)}
                      showDetailButton={option.setPickup}
                      onClickDetail={() => setShowPickupModal(true)}
                    />
                  ))}
                  <StyledButton title="Weiter" onClick={handleNextFromDelivery}/>
                </>
              </AccordionSection>
              <AccordionSection
                isOpen={isPaymentOpen}
                header="Zahlung"
                onHeaderClick={() => goToStep(3)}
              >
                <>
                  {paymentOptions.map(option => (
                    <PaymentOption
                      key={option.id}
                      headline={option.headline}
                      logoSrc={option.imageSrc}
                      isSelected={selectedPaymentId === option.id}
                      onClick={() => setSelectedPaymentId(option.id)}
                    >
                      <div>{option.description}</div>
                    </PaymentOption>
                  ))}
                  <StyledButton title="Bestellung aufgeben" onClick={() => console.log("submit")}/>
                </>
              </AccordionSection>
            </Col>
          </Row>
        </Col>
        <Col xl={5} lg={5} md={5} sm={12} xs={12}>
          <CheckoutSummary
            products={products.map((product: any) => ({
              ...product,
              unitPrice: product.unitPrice ?? product.price ?? 0,
              totalPrice: product.totalPrice ?? ((product.unitPrice ?? product.price ?? 0) * (product.qty ?? 1)),
              available: product.available ?? true,
            }))}
            total={total}
            taxes={taxes.map((tax: any) => ({ ...tax, rate: String(tax.rate) }))}
            onQuantityChange={(productId: string, qty: number) => {
              // Implement quantity change logic here
              console.log("Quantity changed", productId, qty);
            }}
            onDelete={(productId: string) => {
              // Implement delete logic here
              console.log("Delete product", productId);
            }}
          />
        </Col>
      </Row>
      <CustomOffcanvas
        show={showPickupModal}
        onHide={() => setShowPickupModal(false)}
        title={"Standorte"}
        imageUrl={bg_05}
        children={
          <PickupStoreModal
            preparationDays={requiredPreparationDays}
            stores={stores}
            selectedStore={selectedStore}
            setSelectedStore={setSelectedStore}
          />
        }
      />
    </Container>
  );
};

export default Checkout;